import {
    onboardingBillingAddressSchema,
    OnboardingSubscriptionOrderState,
    type OnboardingBillingAddress,
    type OnboardingSubscriptionOrderWithPlan,
} from '@houzy/schemas/partner-cockpit/onboarding';
import {
    checkoutConfirmLabelKey,
    SubscriptionPaymentMethod,
} from '@houzy/schemas/partner-management/partner-subscription-management';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorMessageKey } from '../../../lib/api';
import { PAYMENT_METHOD_PRESENTATION } from '../../../lib/paymentMethodPresentation';
import { useTrackEvent } from '../../../lib/trackingEvents';
import { useTrackCheckoutCompleted, useTrackCheckoutLoaded } from '../../subscriptionCheckout/checkoutTracking';
import { CheckoutPaymentMethodCard } from '../../subscriptionCheckout/components/CheckoutPaymentMethodCard';
import { CheckoutPlanCard } from '../../subscriptionCheckout/components/CheckoutPlanCard';
import { CheckoutPricingCard } from '../../subscriptionCheckout/components/CheckoutPricingCard';
import { CheckoutTermsConsent } from '../../subscriptionCheckout/components/CheckoutTermsConsent';
import { orderQueryOptions, useSelectPlanMutation, useConfirmOrderMutation } from '../api/subscriptionApi';
import { InvoiceDetailsForm } from '../components/InvoiceDetailsForm';
import { WEBHOOK_POLL_WINDOW_MS, webhookPollInterval } from '../webhookPolling';

export type SubscriptionCheckoutStepProps = {
    payment?: 'success' | 'cancelled';
};

const EMPTY_INVOICE_DETAILS: OnboardingBillingAddress = {
    companyName: '',
    billingEmail: '',
    street: '',
    nr: '',
    zip: '',
    city: '',
};

export function SubscriptionCheckoutStep({ payment }: SubscriptionCheckoutStepProps): ReactElement {
    const navigate = useNavigate();
    const pollUntil = useRef(Date.now() + WEBHOOK_POLL_WINDOW_MS);
    const { data: order } = useSuspenseQuery({
        ...orderQueryOptions(),
        refetchInterval: (query): number | false =>
            webhookPollInterval({
                state: query.state.data?.state,
                payment,
                pollUntil: pollUntil.current,
                now: Date.now(),
            }),
    });

    useTrackCheckoutCompleted({
        source: 'self-onboarding',
        hasOrder: order.state !== OnboardingSubscriptionOrderState.AwaitingPlanSelection,
        isConfirmed: order.state === OnboardingSubscriptionOrderState.Confirmed,
        payment,
    });

    useEffect(() => {
        if (order.state === OnboardingSubscriptionOrderState.Confirmed) {
            void navigate({ to: '/onboarding/subscription/confirmed', replace: true });
        }
        if (order.state === OnboardingSubscriptionOrderState.AwaitingPlanSelection) {
            void navigate({ to: '/onboarding/subscription', replace: true });
        }
    }, [order.state, navigate]);

    if (order.state === OnboardingSubscriptionOrderState.AwaitingPlanSelection) {
        return <></>;
    }

    return <CheckoutForm order={order} payment={payment} />;
}

function CheckoutForm({
    order,
    payment,
}: {
    order: OnboardingSubscriptionOrderWithPlan;
    payment?: 'success' | 'cancelled';
}): ReactElement {
    const { t } = useTranslation();
    const [accepted, setAccepted] = useState(false);
    const [showConsentError, setShowConsentError] = useState(false);
    // The server seeds this from the company step, so an empty form only happens when that step is itself empty.
    const [invoiceDetails, setInvoiceDetails] = useState<OnboardingBillingAddress>(
        () => order.invoiceDetails ?? EMPTY_INVOICE_DETAILS,
    );
    const [invoiceDetailsValid, setInvoiceDetailsValid] = useState(
        () => onboardingBillingAddressSchema.safeParse(order.invoiceDetails).success,
    );
    const queryClient = useQueryClient();
    const selectPlan = useSelectPlanMutation();
    const confirm = useConfirmOrderMutation();
    const track = useTrackEvent();

    const isInvoice = order.paymentMethod === SubscriptionPaymentMethod.Invoice;
    const awaitingWebhook =
        payment === 'success' && order.state === OnboardingSubscriptionOrderState.AwaitingPaymentDetails;
    useTrackCheckoutLoaded({ source: 'self-onboarding', payment });

    const submit = (): void => {
        if (!accepted) {
            setShowConsentError(true);
            return;
        }
        confirm.mutate(
            { termsAccepted: true, ...(isInvoice ? { invoiceDetails } : {}) },
            {
                onSuccess: async ({ redirectUrl }) => {
                    track('Checkout Submitted', { checkoutSource: 'self-onboarding' });
                    if (redirectUrl) {
                        window.location.assign(redirectUrl);
                        return;
                    }
                    await queryClient.invalidateQueries({ queryKey: orderQueryOptions().queryKey });
                },
            },
        );
    };

    const ctaKey = checkoutConfirmLabelKey(order);
    const submitDisabled = awaitingWebhook || (isInvoice && !invoiceDetailsValid);

    return (
        <Stack spacing={4}>
            <Stack spacing={1}>
                <Link component={RouterLink} to="/onboarding/subscription" variant="body2" underline="hover">
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                        <ArrowBackIcon sx={{ fontSize: 16 }} />
                        {t('ONBOARDING.SUBSCRIPTION.BACK_TO_PLANS')}
                    </Stack>
                </Link>
                <Typography variant="h4">{t('CHECKOUT.HEADING')}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('CHECKOUT.SUBHEADING', { name: order.greetingName })}
                </Typography>
            </Stack>

            {awaitingWebhook && <Alert severity="info">{t('CHECKOUT.CONFIRMING_NOTICE')}</Alert>}
            {payment === 'cancelled' && <Alert severity="warning">{t('CHECKOUT.RETRY_NOTICE')}</Alert>}

            <Stack spacing={2}>
                <CheckoutPlanCard plan={order.plan} agreedStartAt={order.agreedStartAt} showAdvantages={false} />
                <CheckoutPricingCard offer={order} />
                <CheckoutPaymentMethodCard
                    offer={order}
                    selectableMethods={order.selectablePaymentMethods}
                    onMethodChange={(paymentMethod) => {
                        selectPlan.mutate({
                            planUuid: order.planUuid,
                            billingCadence: order.plan.billingCadence,
                            paymentMethod,
                        });
                    }}
                    invoiceDetails={
                        isInvoice ? (
                            <InvoiceDetailsForm
                                initial={invoiceDetails}
                                onChange={(next, valid) => {
                                    setInvoiceDetails(next);
                                    setInvoiceDetailsValid(valid);
                                }}
                            />
                        ) : undefined
                    }
                />

                <Stack spacing={1}>
                    <CheckoutTermsConsent
                        commitmentLengthInMonths={order.plan.commitmentLengthInMonths}
                        autoProlongation={order.autoProlongation}
                        accepted={accepted}
                        onAcceptedChange={(next) => {
                            setAccepted(next);
                            if (next) {
                                setShowConsentError(false);
                            }
                        }}
                        showError={showConsentError}
                    />

                    {selectPlan.isError && (
                        <Alert severity="error">
                            {t(apiErrorMessageKey(selectPlan.error, 'CHECKOUT.ERRORS.METHOD_SWITCH_FAILED'))}
                        </Alert>
                    )}

                    {confirm.isError && (
                        <Alert severity="error">
                            {t(apiErrorMessageKey(confirm.error, 'CHECKOUT.ERRORS.CONFIRM_FAILED'))}
                        </Alert>
                    )}

                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        loading={confirm.isPending || awaitingWebhook}
                        disabled={submitDisabled}
                        startIcon={PAYMENT_METHOD_PRESENTATION[order.paymentMethod].icon}
                        onClick={submit}>
                        {t(ctaKey)}
                    </Button>
                </Stack>

                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                    <LockOutlinedIcon sx={{ fontSize: 12, color: 'action.active', flexShrink: 0 }} />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center', fontWeight: 500, fontSize: 11, letterSpacing: '0.3px' }}>
                        {t('CHECKOUT.ENCRYPTED')}
                        {order.cancellableUntil &&
                            ` ${t('CHECKOUT.CANCEL_BEFORE', { date: new Date(order.cancellableUntil) })}`}
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    );
}
